/**
 * ConnectorWizard — registry-driven, step-by-step setup for any DB connector.
 *
 * 4 steps:
 *   1. Configure   — dynamic form rendered from driver.config_schema()
 *   2. Test        — live POST /test, shows latency + server version
 *   3. Pick tables — schemas → tables → columns (with cursor column picker)
 *   4. Relationships — auto-inferred joins (add / delete / edit confidence)
 *   → Ingest       — triggers auto-dashboard and returns to the list.
 *
 * Works for both new connectors and existing ones (resume at step 3 via
 * the `resume` prop).
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import ConnectorIcon from "@/components/data/ConnectorIcon";
import { TablesStep, RelationshipsStep, ConfigureStep, TestStep, AccountsStep, DateRangeStep, MappingStep, WizardFooter } from "@/components/data/wizard/WizardSteps";
import { FileBrowserStep } from "@/components/data/wizard/FileBrowserStep";
import { RedshiftWorkgroupStep } from "@/components/data/wizard/RedshiftWorkgroupStep";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toTitleLabel } from "@/lib/label-case";
import { useOAuthProviders } from "@/lib/oauth-providers";
import {
    ChevronLeft, ChevronRight, CheckCircle2, XCircle, PlugZap, TableProperties,
    Database, Loader2, Link2, Sparkles, Plus, Trash2, GitBranch, Zap, Clock, Layers,
    Users, KeyRound, Calendar, Wand2, FolderTree,
} from "lucide-react";

// iter-118c (legacy) / iter-119u — every connector now exposes
// `wizard_steps` via the catalog API (role-trimmed server-side for non-SA
// users). The wizard renders steps in that exact order; this static map
// only provides labels + icons for each known step id. Drivers that don't
// declare `wizard_steps` fall back to the legacy 4-step DB flow.
// Phase-1 (Refresh-Token UX) — mirrors the backend sentinel from
// core.oauth_secret_store.SAVED_SENTINEL. When the wizard sees this
// value on a `type=password` field it renders "Saved · Replace" instead
// of an empty-looking password box, so users know a credential is on
// file and don't feel they must re-enter it.
export const SAVED_SENTINEL = "__ANALYZIO_SAVED__";
export const isSavedSentinel = (v) => v === SAVED_SENTINEL || v === "***";
const STEP_META = {
    configure:     { label: "Configure",         icon: PlugZap },
    test:          { label: "Test connection",   icon: Zap },
    accounts:      { label: "Select accounts",   icon: Users },
    date_range:    { label: "Date range",        icon: Calendar },
    tables:        { label: "Pick tables",       icon: TableProperties },
    // iter-121.170 — file-tree step for cloud-storage drivers (Amazon
    // S3, SFTP, and any future Google Drive / Azure Blob / Dropbox
    // driver). Rendered by `<FileBrowserStep />` — the driver exposes
    // `browse(cfg, path)` on the backend and the FE lazy-expands one
    // folder at a time. Selection is stored in the same `selected`
    // array as `tables`, using `{schema:"", name:<file_path>}` so
    // the existing ingest / relationships / mapping steps work
    // unchanged.
    files:         { label: "Pick files",        icon: FolderTree },
    // iter-121.165 — Two-step end for SQL flows.
    // Drivers that include BOTH `relationships` AND `mapping` in
    // wizard_steps (SQL Server / MySQL / Postgres / any DB driver)
    // get step 4 relabelled to "Relationships" (join review) and
    // step 5 to "Confirm mapping" (Data Model picker + Ingest).
    // Drivers with `relationships` only (marketing OAuth flows) keep
    // the merged "Confirm mapping" label — see `_stepsFor`.
    relationships: { label: "Relationships",     icon: GitBranch },
    mapping:       { label: "Confirm mapping",   icon: Wand2 },
};

const LEGACY_DB_STEPS = ["configure", "test", "tables", "relationships"];

function _stepsFor(driver) {
    const ids = (driver?.wizard_steps && driver.wizard_steps.length > 0)
        ? driver.wizard_steps
        : LEGACY_DB_STEPS;
    const hasMapping = ids.includes("mapping");
    return ids
        .map((id) => {
            const meta = STEP_META[id];
            if (!meta) return null;
            // iter-121.165 — Merged "Confirm mapping" label ONLY when
            // the driver doesn't also declare a mapping step. Keeps
            // marketing flows visually identical to Phase-1 while
            // SQL flows get the proper two-step split. No per-driver
            // hardcoding.
            if (id === "relationships" && !hasMapping) {
                return { key: id, ...meta, label: "Confirm mapping" };
            }
            if (id === "accounts" && driver?.key === "redshift") {
                return { key: id, ...meta, label: "Select workgroup" };
            }
            return { key: id, ...meta };
        })
        .filter(Boolean);
}

/**
 * Initial wizard config = driver field defaults + any saved config.
 * NEW connections always start in LIVE mode (`use_sample_data: false`) so
 * the Configure page opens ready for real setup; the sample-data switch is
 * an opt-in at the bottom of the page. Explicit `false` matters: some
 * drivers (YAML) treat a missing key as sample mode. Existing / resumed
 * connections keep whatever they were saved with.
 */
function initialConfig(driver, resume) {
    const init = {};
    (driver?.fields || []).forEach((f) => { init[f.key] = f.default ?? ""; });
    if (!resume && Object.prototype.hasOwnProperty.call(init, "use_sample_data")) {
        init.use_sample_data = false;
    }
    return { ...init, ...(resume?.config || {}) };
}

export default function ConnectorWizard({ driver, resume, onClose, onDone, onBack }) {
    const open = !!driver;
    const isResume = !!resume;
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [stepIdx, setStepIdx] = useState(0);
    const [busy, setBusy] = useState(false);
    // Connector state
    const [connectorId, setConnectorId] = useState(resume?.id || null);
    const [name, setName] = useState(resume?.name || driver?.name || "");
    const [config, setConfig] = useState(() => initialConfig(driver, resume));
    // Configure-step validation: keys flagged as missing on Next
    // (`__name` = connection name). Cleared as the user fills them in.
    const [missingKeys, setMissingKeys] = useState(() => new Set());
    // Test result
    const [testResult, setTestResult] = useState(null);
    // Browse state
    const [schemas, setSchemas] = useState([]);
    const [expandedSchemas, setExpandedSchemas] = useState({});
    const [tablesBySchema, setTablesBySchema] = useState({});
    const [selected, setSelected] = useState(resume?.selected_tables || []); // [{schema, name, incremental_col}]
    // iter-118c: per-connector account discovery (Meta / Google Ads / GA4)
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountsError, setAccountsError] = useState(null);
    const [selectedAccountIds, setSelectedAccountIds] = useState(() => {
        const ids = resume?.config?.selected_account_ids;
        return Array.isArray(ids) ? ids : (resume?.config?.ad_account_id ? [resume.config.ad_account_id] : []);
    });
    // STEPS is now driver-declared (catalog returns role-trimmed list).
    // Recompute when driver swaps OR its wizard_steps payload changes.
    const STEPS = React.useMemo(() => _stepsFor(driver), [driver]);
    const hasTablesStep = STEPS.some((s) => s.key === "tables");
    const hasFilesStep = STEPS.some((s) => s.key === "files");
    const hasRelationshipsStep = STEPS.some((s) => s.key === "relationships");
    // iter-121.170 — File-browser state for cloud-storage drivers.
    // Same tree-cache pattern as tablesBySchema: `filesByPath[""]` holds
    // the root listing, `filesByPath["orders/"]` holds the contents of
    // the "orders/" folder, etc. `expandedPaths[folderPath] = true` when
    // the user has clicked to expand a folder.
    const [filesByPath, setFilesByPath] = useState({});
    const [expandedPaths, setExpandedPaths] = useState({});
    // Relationships
    const [relationships, setRelationships] = useState(resume?.relationships || []);
    // iter-2026 — explicit fact/base table for the combined (joined) view.
    // Qualified "schema.table" | null. null => backend uses the row-count
    // heuristic (largest table), preserving legacy behavior.
    const [factTable, setFactTable] = useState(resume?.fact_table || null);
    // Schedule (Step 4 inline option)
    const [enableSchedule, setEnableSchedule] = useState(!!(resume?.schedule?.enabled));
    const [scheduleHours, setScheduleHours] = useState(resume?.schedule?.every_hours || 24);
    // iter-119bd Phase 4 — Track datasets created by finalizeIngest so the
    // mapping step can render one SchemaMatchPreview per dataset.
    const [ingestedDatasets, setIngestedDatasets] = useState([]);
    // iter-119u — date_range step state (writes to sync_settings.date_range).
    const [dateRange, setDateRange] = useState(() => {
        const dr = resume?.sync_settings?.date_range;
        if (dr && (dr.kind === "relative" || dr.kind === "absolute")) return dr;
        return { kind: "relative", lookback_days: 180 };
    });
    // iter-119: OAuth pre-fill state — fields the wizard knows are sourced
    // from Settings → OAuth, the masked current values, and an
    // "override for this connector" toggle.
    const [oauthMerge, setOauthMerge] = useState({ fields: [], values: {}, has_credentials: false });
    const [oauthOverride, setOauthOverride] = useState({});
    // Phase-2 (Multi-cred OAuth) — list of credential sets available for
    // this driver's OAuth provider. Populated on driver change; consumed
    // by the ConfigureStep dropdown.
    const [credentialSets, setCredentialSets] = useState([]);
    const [yamlOauthConnected, setYamlOauthConnected] = useState(false);
    const isYamlOAuth = (driver?.is_yaml || !!driver?.auth_type) && ["oauth2_authcode", "oauth2_pkce"].includes(driver?.auth_type || driver?.spec?.auth?.type);

    useEffect(() => {
        if (!driver?.key || !isYamlOAuth) {
            setYamlOauthConnected(false);
            return;
        }
        let cancelled = false;
        api.get(`/yaml-connectors/oauth/status/${driver.key}`)
            .then((r) => {
                if (!cancelled) setYamlOauthConnected(!!r.data?.connected);
            })
            .catch(() => {
                if (!cancelled) setYamlOauthConnected(false);
            });
        return () => { cancelled = true; };
    }, [driver?.key, isYamlOAuth]);
    // iter-121.163 Phase 2 (Data Model selector) — list of data models
    // matching the driver's `target_model_slug` category. The user
    // picks one on the "Confirm mapping" step; the selection is stored
    // on the connector's `sync_settings.data_model_slug` and overrides
    // the driver-level writeback target at ingest time.
    const [dataModels, setDataModels] = useState([]);
    const [dataModelSlug, setDataModelSlug] = useState(
        resume?.sync_settings?.data_model_slug || null,
    );

    // Load oauth-merged-fields when driver changes so we know which inputs
    // to lock + which "Sourced from OAuth" hint to render.
    // Multi-cred (Phase 2): also re-fetch when the selected credential
    // set changes, so the wizard shows the SELECTED app's Client ID +
    // Secret instead of always the tenant default. Universal — works for
    // Google Ads, GA4, Meta Ads and every future OAuth provider.
    useEffect(() => {
        if (!driver?.key) {
            setOauthMerge({ fields: [], values: {}, has_credentials: false });
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const setId = (config && config.oauth_credential_set_id) || "";
                const qs = setId ? `&set_id=${encodeURIComponent(setId)}` : "";
                const r = await api.get(`/data/connectors-aux/oauth-merged-fields?key=${driver.key}${qs}`);
                if (cancelled) return;
                setOauthMerge(r.data || { fields: [], values: {}, has_credentials: false });
            } catch {
                // No-op — older drivers don't have merge mapping.
            }
        })();
        return () => { cancelled = true; };
    }, [driver?.key, config?.oauth_credential_set_id]);

    // Phase-2 (Multi-cred) — fetch this provider's saved credential sets
    // so the ConfigureStep can render an "OAuth Credential" dropdown.
    // Auto-select the connector's stored set_id, else the default set,
    // else the sole active set. Extracted into a callback so it can also be
    // re-run right after a save — that's what surfaces a credential set the
    // user just created by typing creds directly into the wizard (two-way
    // sync), selecting it in the dropdown immediately.
    // OAuth connectors come from the registry (native + YAML OAuth) — no hardcoded list.
    const { keys: CRED_SET_PROVIDERS, nativeKeys: NATIVE_OAUTH_FLOW_KEYS } = useOAuthProviders();
    const loadCredentialSets = React.useCallback(async (selectId) => {
        const p = CRED_SET_PROVIDERS.includes(driver?.key) ? driver.key : null;
        if (!p) { setCredentialSets([]); return; }
        try {
            const r = await api.get(`/oauth/credentials/${p}/sets`);
            const sets = (r.data && r.data.sets) || [];
            setCredentialSets(sets);
            setConfig((c) => {
                // If the caller asked to select a specific set (e.g. the one
                // the backend just created from typed creds), honour it when
                // it's a real active set.
                if (selectId && sets.some((s) => s.id === selectId && !s.deactivated)) {
                    return c.oauth_credential_set_id === selectId ? c : { ...c, oauth_credential_set_id: selectId };
                }
                const chosen = c.oauth_credential_set_id;
                const chosenStillValid = chosen && sets.some(
                    (s) => s.id === chosen && !s.deactivated,
                );
                if (chosenStillValid) return c;
                const active = sets.filter((s) => !s.deactivated);
                const auto =
                    active.find((s) => s.is_default)?.id ||
                    (active.length === 1 ? active[0].id : null);
                if (auto && auto !== chosen) {
                    return { ...c, oauth_credential_set_id: auto };
                }
                return c;
            });
        } catch {
            // Provider tab may not exist yet or user lacks perms — no-op.
            setCredentialSets([]);
        }
    }, [driver?.key, CRED_SET_PROVIDERS]);

    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await loadCredentialSets(); })();
        return () => { cancelled = true; };
    }, [loadCredentialSets]);

    // iter-121.163 Phase 2 — fetch data models in the same category as
    // the driver's target_model_slug. Auto-select the driver's target
    // when the wizard opens if the connector doesn't already have a
    // stored `sync_settings.data_model_slug`. Empty list is fine (older
    // drivers without target_model_slug just get an empty dropdown +
    // the Data Model section stays hidden).
    // Target models come from the binding rules (enforced models listing this
    // connector first), never from category alone. Only a single enforced
    // binding is pre-selected; otherwise the user must choose.
    const [modelTargets, setModelTargets] = useState(null);
    useEffect(() => {
        if (!driver?.target_model_slug && !driver?.table_model_map) { setDataModels([]); setModelTargets(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/data/connectors/model-targets`, { params: { connector_key: driver.key, connection_id: connectorId || undefined } });
                if (cancelled) return;
                const t = r.data;
                setModelTargets(t);
                setDataModels(t.models || []);
                setDataModelSlug((prev) => {
                    const ok = (slug) => (t.models || []).some((m) => m.slug === slug && m.valid) && (!t.enforced.length || t.enforced.includes(slug));
                    // Existing connections created before binding rules keep the target
                    // they route to today (stored choice, else the driver default).
                    const legacy = resume && !resume.model_binding_strict && !resume.model_selection_required;
                    const current = resume?.sync_settings?.data_model_slug || (legacy ? driver.target_model_slug : null);
                    if (legacy && current && (!prev || prev === current)) return current;
                    if (prev && ok(prev)) return prev;
                    return t.default || null;
                });
            } catch {
                setDataModels([]); setModelTargets(null);
            }
        })();
        return () => { cancelled = true; };
    }, [driver?.key, driver?.target_model_slug, connectorId]);

    useEffect(() => {
        if (!driver) return;
        // iter-119b: Manage / resume opens at Configure step (0) — not the
        // table-picker (2) as before — so users can see + edit their saved
        // credentials and account selection. They can still click Next to
        // jump back to whichever step they're interested in.
        setStepIdx(0);
        setConnectorId(resume?.id || null);
        setName(resume?.name || driver.name);
        setConfig(initialConfig(driver, resume));
        setMissingKeys(new Set());
        setTestResult(null);
        setSchemas([]);
        setExpandedSchemas({});
        setTablesBySchema({});
        setSelected(resume?.selected_tables || []);
        setRelationships(resume?.relationships || []);
        setFactTable(resume?.fact_table || null);
        setEnableSchedule(!!(resume?.schedule?.enabled));
        setScheduleHours(resume?.schedule?.every_hours || 24);
        // iter-119u: reset date_range from resume state.
        const dr = resume?.sync_settings?.date_range;
        setDateRange(dr && (dr.kind === "relative" || dr.kind === "absolute")
            ? dr
            : { kind: "relative", lookback_days: 180 });
    }, [driver, resume, isResume]);

    // Prefetch schemas when we land on the Pick-tables step (index varies
    // when the optional Accounts step is present, so key on `step.key`).
    useEffect(() => {
        const cur = STEPS[stepIdx];
        if (cur?.key === "tables" && connectorId && schemas.length === 0 && driver) {
            (async () => {
                setBusy(true);
                try {
                    const r = await api.get(`/data/connectors/${connectorId}/schemas`);
                    const schs = r.data?.schemas || [];
                    setSchemas(schs);
                    // Auto-expand the first schema so users see entity rows immediately
                    // without an extra click — they get the "all checked" UX out-of-box.
                    if (schs.length > 0) {
                        setExpandedSchemas((p) => ({ ...p, [schs[0]]: true }));
                        try {
                            const tr = await api.get(`/data/connectors/${connectorId}/tables`,
                                { params: { schema: schs[0] } });
                            const tables = tr.data?.tables || [];
                            const hiddenSaOnly = tr.data?.hidden_sa_only || [];
                            setTablesBySchema((p) => ({ ...p, [schs[0]]: tables }));
                            if (selected.length === 0 && tables.length > 0) {
                                const preselect = tables.map((t) => {
                                    const updatedAt = (t.columns || []).find((c) => c.name === "updated_at");
                                    const pk = (t.columns || []).find((c) => c.is_pk);
                                    return { schema: schs[0], name: t.name, incremental_col: updatedAt?.name || pk?.name || "" };
                                });
                                // iter-120 round-4 follow-up #8 — non-SA users
                                // never see SA-only tables (Meta Ads breakdown
                                // supplements) in the picker, but they MUST
                                // still ingest them. Silently include each
                                // hidden table in the saved selection so the
                                // backend ingest pulls every breakdown row.
                                for (const h of hiddenSaOnly) {
                                    preselect.push({ schema: h.schema, name: h.name, incremental_col: "" });
                                }
                                setSelected(preselect);
                            }
                        } catch { /* user can still expand manually */ }
                    }
                } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not list schemas");
                } finally { setBusy(false); }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIdx, connectorId]);

    // iter-121.170 — Prefetch the root file listing when the wizard
    // lands on the "Pick files" step. Storage drivers (S3, SFTP)
    // implement `browse(cfg, path)` and the wizard lazy-expands each
    // folder on click via the same endpoint.
    useEffect(() => {
        const cur = STEPS[stepIdx];
        if (cur?.key !== "files" || !connectorId || !driver) return;
        if (filesByPath[""] !== undefined) return;
        (async () => {
            setBusy(true);
            try {
                const r = await api.get(`/data/connectors/${connectorId}/browse`,
                    { params: { path: "" } });
                const items = r.data?.items || [];
                setFilesByPath((p) => ({ ...p, "": items }));
            } catch (e) {
                const msg = e?.response?.data?.detail || e?.message || "Could not list files";
                toast.error(msg);
                // Store an empty array so the step renders an empty-state
                // instead of an infinite "Loading…" spinner.
                setFilesByPath((p) => ({ ...p, "": [] }));
            } finally { setBusy(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIdx, connectorId]);

    // iter-121.170 — Handlers for the FileBrowserStep. Toggle folder
    // expansion (lazy-load its listing on first expand); toggle file
    // selection into the shared `selected` array using `schema=""` and
    // `name=<file_path>` so the existing ingest / relationships /
    // mapping code paths reuse it verbatim.
    const toggleFolder = React.useCallback(async (folderPath) => {
        const currentlyExpanded = !!expandedPaths[folderPath];
        setExpandedPaths((p) => ({ ...p, [folderPath]: !currentlyExpanded }));
        if (!currentlyExpanded && filesByPath[folderPath] === undefined && connectorId) {
            setBusy(true);
            try {
                const r = await api.get(`/data/connectors/${connectorId}/browse`,
                    { params: { path: folderPath } });
                setFilesByPath((p) => ({ ...p, [folderPath]: r.data?.items || [] }));
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not list files");
                setFilesByPath((p) => ({ ...p, [folderPath]: [] }));
            } finally { setBusy(false); }
        }
    }, [connectorId, expandedPaths, filesByPath]);

    const isFileSelected = React.useCallback((path) => {
        return selected.some((s) => (s.schema || "") === "" && s.name === path);
    }, [selected]);

    const toggleFile = React.useCallback((filePath) => {
        setSelected((prev) => {
            const idx = prev.findIndex((x) => (x.schema || "") === "" && x.name === filePath);
            if (idx >= 0) {
                const next = [...prev];
                next.splice(idx, 1);
                return next;
            }
            return [...prev, { schema: "", name: filePath, incremental_col: "" }];
        });
    }, []);

    // Selecting a folder = selecting every SUPPORTED file below it.
    // Called after the folder listing has loaded so we know what's inside.
    const toggleFolderSelection = React.useCallback(async (folderPath) => {
        let items = filesByPath[folderPath];
        if (items === undefined && connectorId) {
            try {
                const r = await api.get(`/data/connectors/${connectorId}/browse`,
                    { params: { path: folderPath } });
                items = r.data?.items || [];
                setFilesByPath((p) => ({ ...p, [folderPath]: items }));
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not list files");
                return;
            }
        }
        const supported = (items || []).filter((i) => i.type === "file" && i.supported);
        if (supported.length === 0) {
            toast.info(`No supported files in "${folderPath || 'root'}".`);
            return;
        }
        // If ANY of the supported files are unselected → select all;
        // otherwise (all currently selected) → deselect all.
        setSelected((prev) => {
            const inSet = new Set(prev.filter((s) => (s.schema || "") === "").map((s) => s.name));
            const anyUnselected = supported.some((f) => !inSet.has(f.path));
            if (anyUnselected) {
                const next = [...prev];
                for (const f of supported) {
                    if (!inSet.has(f.path)) next.push({ schema: "", name: f.path, incremental_col: "" });
                }
                return next;
            }
            // All selected → deselect
            const paths = new Set(supported.map((f) => f.path));
            return prev.filter((s) => !((s.schema || "") === "" && paths.has(s.name)));
        });
    }, [filesByPath, connectorId]);

    // iter-118c: fetch ad/property accounts when landing on the Accounts step.
    useEffect(() => {
        const cur = STEPS[stepIdx];
        if (cur?.key !== "accounts" || !driver) return;
        let cancelled = false;
        (async () => {
            setAccountsLoading(true);
            setAccountsError(null);
            try {
                const r = await api.post("/data/connectors-aux/discover-accounts", {
                    connector_key: driver.key,
                    config: config,
                    // Phase-1 (Refresh-Token UX) — send the saved
                    // connector id so the server can hydrate real
                    // credentials from the DB. Without this, the wizard's
                    // sentinel-filled cfg reaches the driver and Google
                    // rejects it as invalid_client on re-visits.
                    connector_id: connectorId || undefined,
                });
                if (cancelled) return;
                if (r.data?.error) {
                    setAccountsError(r.data.error);
                    setAccounts([]);
                } else {
                    setAccounts(r.data?.accounts || []);
                    // Pre-select whatever the connector was created with.
                    if (selectedAccountIds.length === 0 && config.ad_account_id) {
                        setSelectedAccountIds([config.ad_account_id]);
                    }
                }
            } catch (e) {
                if (!cancelled) {
                    setAccountsError(e?.response?.data?.detail || "Failed to discover accounts");
                }
            } finally {
                if (!cancelled) setAccountsLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIdx, driver?.key]);

    // iter-119bo — Auto-resume polling on wizard mount. If the user
    // reloaded the page while a connector ingest was running, restore
    // the polling loop so the wizard ends in the same place it would
    // have ended without the reload. Declared HERE (before the
    // `if (!driver) return null` early return) so it complies with
    // React's rules-of-hooks invariant.
    useEffect(() => {
        if (!connectorId) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/data/connectors/ingest-jobs/active");
                const active = (r.data?.jobs || []).find((j) => j.connector_id === connectorId);
                if (!active || cancelled) return;
                toast.info("Resuming in-flight ingest…", {
                    description: `Job ${String(active.id).slice(0, 8)}… picked up from ${active.status}`,
                    duration: 4000,
                });
                setBusy(true);
                try {
                    const result = await pollIngestJob(active.id, { table_count: active.progress?.tables_total });
                    if (cancelled) return;
                    const datasets = result.datasets || [];
                    const joined = result.joined;
                    const allDs = joined ? [...datasets, joined] : datasets;
                    setIngestedDatasets(allDs);
                    toast.success(`Ingest finished — ${datasets.length} dataset${datasets.length !== 1 ? "s" : ""}${joined ? " + joined view" : ""}`);
                    window.dispatchEvent(new CustomEvent("data:ingested", {
                        detail: { connectorId, datasets: datasets.length },
                    }));
                    window.dispatchEvent(new CustomEvent("dashboard:refresh"));
                    const mappingIdx = STEPS.findIndex((s) => s.key === "mapping");
                    if (mappingIdx >= 0) setStepIdx(mappingIdx);
                } catch (err) {
                    if (!cancelled) {
                        if (err?.isPollTimeout) {
                            toast.info("Ingest still running", { description: err.message, duration: 10000 });
                        } else {
                            toast.error(err?.message || "Resumed ingest failed");
                        }
                    }
                }
            } catch {
                // Active-jobs endpoint may 401/403 for some roles — ignore.
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectorId]);

    if (!driver) return null;
    const step = STEPS[stepIdx];

    // ---------- Step 1 : Configure ----------
    // Conditional visibility — a field with `show_if` only renders when every
    // referenced field currently holds one of the allowed values. Generic,
    // driver-agnostic (used by e.g. HubSpot's Auth Mode).
    const fieldVisible = (f) => {
        const cond = f.show_if;
        if (!cond) return true;
        return Object.entries(cond).every(([k, allowed]) => {
            const arr = Array.isArray(allowed) ? allowed : [allowed];
            return arr.includes(config[k]);
        });
    };
    const fieldGroups = (() => {
        const groups = {};
        (driver.fields || []).filter(fieldVisible).forEach((f) => {
            groups[f.group || "Connection"] = groups[f.group || "Connection"] || [];
            groups[f.group || "Connection"].push(f);
        });
        return groups;
    })();

    const clearMissing = (k) => setMissingKeys((prev) => {
        if (!prev.has(k)) return prev;
        const next = new Set(prev);
        next.delete(k);
        return next;
    });
    const updateCfg = (k, v) => {
        setConfig((c) => ({ ...c, [k]: v }));
        clearMissing(k);
        // Switching to sample mode makes every live field optional (the
        // connection name is still required, so keep its flag).
        if (k === "use_sample_data" && v) {
            setMissingKeys((prev) => (prev.has("__name") ? new Set(["__name"]) : new Set()));
        }
    };
    const setNameAndClear = (v) => { setName(v); clearMissing("__name"); };
    const isSample = !!config.use_sample_data;
    const legacyOauthProvider = NATIVE_OAUTH_FLOW_KEYS.includes(driver?.key) ? driver.key : null;
    const oauthProvider = legacyOauthProvider || (isYamlOAuth ? driver?.key : null);
    const tokenField = oauthProvider === "meta_ads" ? "access_token" : "oauth_refresh_token";
    const hasOAuthToken = isYamlOAuth ? yamlOauthConnected : !!config[tokenField];

    const startOAuth = async () => {
        if (!oauthProvider) return;
        setBusy(true);
        try {
            if (isYamlOAuth) {
                // Typed values win; masked/sourced values resolve from the selected credential set server-side.
                const typed = (v) => { const t = (v || "").trim(); return t && t !== SAVED_SENTINEL && !t.startsWith("••••") ? t : ""; };
                const cid = typed(config.client_id || config.oauth_client_id || config.app_id);
                const secret = typed(config.client_secret || config.oauth_client_secret || config.app_secret);
                const setId = config.oauth_credential_set_id || null;
                if ((!cid || !secret) && !setId && !oauthMerge?.has_credentials) {
                    toast.error("Please enter Client ID and Client Secret in Step 1 (Configure) first.");
                    return;
                }
                const r = await api.post("/yaml-connectors/oauth/start", {
                    connector_key: driver.key,
                    oauth_client_id: cid,
                    oauth_client_secret: secret,
                    oauth_credential_set_id: setId,
                });
                const popup = window.open(r.data.authorize_url, "analyzio_yaml_oauth", "width=640,height=720");
                if (!popup) { toast.error("Popup blocked — allow popups for this site."); return; }
                const handler = async (ev) => {
                    if (!ev.data || ev.data.type !== "analyzio_yaml_oauth") return;
                    window.removeEventListener("message", handler);
                    if (ev.data.error) { toast.error(ev.data.msg || "OAuth failed"); return; }
                    toast.success("OAuth connected");
                    setYamlOauthConnected(true);
                };
                window.addEventListener("message", handler);
            } else {
                const id = await saveOrUpdateConnector();
                const r = await api.get(`/oauth/${oauthProvider}/start`, { params: { connector_id: id } });
                const popup = window.open(r.data.authorize_url, "analyzio_oauth", "width=640,height=720");
                if (!popup) { toast.error("Popup blocked — allow popups for this site."); return; }
                // Poll for token landing
                const handler = async (ev) => {
                    if (!ev.data || ev.data.type !== "analyzio_oauth") return;
                    window.removeEventListener("message", handler);
                    if (ev.data.error) { toast.error(ev.data.msg || "OAuth failed"); return; }
                    toast.success("OAuth connected");
                    // Refresh connector config from server
                    try {
                        const cr = await api.get(`/data/connectors/${id}`);
                        setConfig({ ...config, ...(cr.data.config || {}) });
                    } catch { /* noop */ }
                };
                window.addEventListener("message", handler);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Failed to start OAuth");
        } finally { setBusy(false); }
    };

    const saveOrUpdateConnector = async () => {
        setBusy(true);
        try {
            if (connectorId) {
                await api.patch(`/data/connectors/${connectorId}`, { name, config });
                return connectorId;
            }
            const r = await api.post("/data/connectors", {
                connector_key: driver.key, name: name || driver.name, config,
            });
            setConnectorId(r.data.id);
            return r.data.id;
        } catch (e) {
            // Duplicate-connection guard (req #1): the connection already
            // exists for this account/project. We only re-throw here — the
            // caller (onNext / OAuth start) surfaces a single toast using the
            // API's `detail`, so we must NOT toast again or it shows twice.
            throw e;
        } finally {
            setBusy(false);
        }
    };

    /**
     * iter-119bo + iter-121.3 — Poll an async ingest job until it terminates.
     *
     * Hardened (Feb 2026): a single transient blip in the poll request
     * (ingress 502, DNS hiccup, brief 404 right after job creation, etc.)
     * USED to throw out of the entire loop → "Ingest failed" toast even
     * when the BG task kept running and successfully wrote every table.
     * We now tolerate up to N consecutive failures (env-tunable) before
     * giving up so the loop survives momentary network noise.
     *
     * Shared by `finalizeIngest` (fresh ingest) and the auto-resume effect
     * (mount-time resume after page reload). Returns the result dict
     * (`{datasets, joined}`) on success; throws on failed/cancelled/timeout.
     */
    const pollIngestJob = async (jobId, { table_count } = {}) => {
        const POLL_TIMEOUT_MS = Number(process.env.REACT_APP_INGEST_POLL_TIMEOUT_MS) || 900000; // 15 min default (Meta Ads live ingests can take 5-10 min)
        const MAX_CONSECUTIVE_FAILURES = Number(process.env.REACT_APP_INGEST_POLL_MAX_FAILS) || 6;
        const startedAt = Date.now();
        let delay = 1500;
        let consecutiveFailures = 0;
        while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
            await new Promise((res) => setTimeout(res, delay));
            delay = Math.min(delay + 1500, 6000);
            let jr;
            try {
                jr = await api.get(`/data/connectors/${connectorId}/ingest-jobs/${jobId}`);
                consecutiveFailures = 0;
            } catch (pollErr) {
                consecutiveFailures += 1;
                if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                    // Genuine outage — surface it. Use the server detail
                    // when available so the user sees WHY (e.g. 401 logged
                    // out, 403 lost access) instead of a generic "failed".
                    const detail = pollErr?.response?.data?.detail || pollErr?.message;
                    throw new Error(detail || `Lost connection to ingest job ${jobId}`);
                }
                // Transient — back off harder and retry on the next tick.
                delay = Math.min(delay + 1500, 8000);
                continue;
            }
            const status = jr.data?.status;
            if (status === "succeeded") {
                return jr.data.result || { datasets: [], joined: null };
            }
            if (status === "failed" || status === "cancelled") {
                // Surface the BG runner's REAL error (token expired,
                // upstream Meta/GA 4xx, etc.) — not a generic toast.
                throw new Error(jr.data?.error || `Ingest ${status}`);
            }
            const prog = jr.data?.progress;
            if (prog?.current_table) {
                toast.message(`Ingest progress`, {
                    description: `${prog.tables_done || 0}/${prog.tables_total || table_count || "?"} tables · current: ${prog.current_table}`,
                    id: `ingest-progress-${jobId}`,
                    duration: 3500,
                });
            }
        }
        // Timed out — but the BG task may still be running. Don't shout
        // "Ingest failed" at the user. They can resume polling later from
        // the Imports panel or just re-open the wizard.
        const timeoutErr = new Error("Ingest is still running after the polling window. Open the Imports panel or this wizard again to resume tracking.");
        timeoutErr.isPollTimeout = true;
        throw timeoutErr;
    };

    // iter-119bo — Auto-resume polling on wizard mount. (Implementation
    // moved up to before the `if (!driver) return null` early return
    // to satisfy React rules-of-hooks.)

    const finalizeIngest = async ({ selectedTables = null } = {}) => {
        if (isSuperAdmin && modelTargets && !dataModelSlug) {
            toast.error(modelTargets.enforced.length > 1
                ? `Choose the target data model — ${driver.name || driver.key} is bound to ${modelTargets.enforced.length} models.`
                : "Choose the target data model in Confirm mapping before ingesting.");
            return;
        }
        setBusy(true);
        try {
            const schedule = enableSchedule
                ? { enabled: true, every_hours: Math.max(1, Number(scheduleHours) || 24), cron: `0 */${Math.max(1, Number(scheduleHours) || 24)} * * *` }
                : { enabled: false };
            // Build the PATCH payload — only include keys relevant to the flow
            // so OA's unified-ingest flow (no tables/relationships steps) doesn't
            // overwrite SA-set defaults with empty arrays.
            const patch = { schedule, sync_settings: { ...(resume?.sync_settings || {}), date_range: dateRange, data_model_slug: dataModelSlug || undefined } };
            if ((hasTablesStep || hasFilesStep) && selectedTables) patch.selected_tables = selectedTables;
            if (hasRelationshipsStep) {
                patch.relationships = relationships;
                patch.fact_table = factTable || null;   // null => backend heuristic
            }
            await api.patch(`/data/connectors/${connectorId}`, patch);
            // Ingest. When the wizard skipped the tables step (OA flow on
            // marketing connectors), `selectedTables` is null → backend
            // re-uses connector.selected_tables (or driver-discovered ones).
            const ingestBody = selectedTables ? { tables: selectedTables } : {};
            // iter-119bn — async ingest. POST returns `{async: true,
            // job_id, status_url}` for long syncs (≥3 tables or ≥60-day
            // lookback by default). Poll the job until status=succeeded
            // or failed, then unwrap the result. Configurable via env
            // (`CONNECTOR_INGEST_ASYNC_MODE`, threshold envs) — frontend
            // is mode-agnostic and works for both flavours.
            let r = await api.post(`/data/connectors/${connectorId}/ingest`, ingestBody);
            if (r.data?.async && r.data?.job_id) {
                const jobId = r.data.job_id;
                const tableTotal = r.data.table_count || 0;
                toast.info(`Ingest running in background${tableTotal ? ` — ${tableTotal} table${tableTotal !== 1 ? "s" : ""}` : ""}`, {
                    description: `Reason: ${r.data.reason || "long-running ingest"}. You can leave this wizard open — we'll update when it's done.`,
                    duration: 5000,
                });
                const pollResult = await pollIngestJob(jobId, { table_count: tableTotal });
                r = { data: pollResult };
            }
            const datasets = r.data?.datasets || [];
            const joined = r.data?.joined;
            // iter-119bd Phase 4 — remember newly-created datasets so the
            // mapping step (when the wizard includes one) can render
            // SchemaMatchPreview for each.
            const allDs = joined ? [...datasets, joined] : datasets;
            setIngestedDatasets(allDs);
            if (datasets.length === 0) {
                const isLive = config?.use_sample_data === false;
                if (isLive) {
                    toast.info("Connected successfully, but no rows were returned", {
                        description: "Your account is reachable, but the selected tables are empty over the configured lookback window. Try a longer lookback (e.g. 90 days), confirm the account has data in its native dashboard, or toggle 'Use sample data' to preview the schema.",
                        duration: 12000,
                    });
                } else {
                    toast.warning("No rows ingested. Check your table selection or incremental cursor.");
                }
            } else {
                const totalRows = datasets.reduce((a, d) => a + (d.rows || 0), 0);
                const parts = [`${totalRows} rows across ${datasets.length} table${datasets.length !== 1 ? "s" : ""}`];
                if (joined) parts.push(`+ joined view (${joined.rows} rows, ${joined.columns} cols)`);
                // iter-121.153 — data-model messaging is SA-only. Non-SA
                // users never see any canonical-model UI or copy; their
                // model is still auto-created / enriched by the backend.
                if (isSuperAdmin) {
                    // Sum every table's write-back per model (each table/grain writes separately).
                    const byModel = {};
                    datasets.forEach((d) => {
                        const mw = d.model_writeback;
                        if (!mw || mw.total == null) return;
                        const k = mw.model_id || mw.model_slug;
                        const a = byModel[k] || (byModel[k] = { name: mw.model_name || mw.model_slug || mw.model_id, inserted: 0, updated: 0, total: 0, tables: 0 });
                        a.inserted += mw.inserted || 0; a.updated += mw.updated || 0; a.total += mw.total || 0; a.tables += 1;
                    });
                    const failed = datasets.filter((d) => d.model_writeback?.failed).map((d) => d.model_writeback);
                    if (failed.length) {
                        const w = failed.reduce((a, m) => a + (m.written || 0), 0);
                        const rj = failed.reduce((a, m) => a + (m.rejected || 0), 0);
                        parts.push(`Model write incomplete for ${failed.length} table${failed.length === 1 ? "" : "s"} — ${w} row${w === 1 ? "" : "s"} written${rj ? `, ${rj} rejected (duplicate record keys)` : ""}; all rows are in the datasets`);
                    }
                    Object.values(byModel).forEach((a) => parts.push(
                        `Wrote ${a.inserted + a.updated}/${a.total} rows into model "${a.name}" from ${a.tables} table${a.tables === 1 ? "" : "s"} (${a.inserted} new, ${a.updated} updated)`));
                }
                // iter-121.3 — partial-success path. The BG runner's
                // recovery probe sets `r.data.partial=true` when data
                // was written but a late side-task (log_activity, etc.)
                // raised. Surface that to the user as a warning toast
                // so they know data is in (not "ingest failed") AND
                // they have the post-write error message for support.
                if (r.data?.partial) {
                    toast.warning(`Ingested ${datasets.length} table${datasets.length !== 1 ? "s" : ""} (with warnings)`, {
                        description: `${parts.join(" · ")} — Note: ${r.data.post_write_error || "a post-write side-task failed but your data is safely stored."}`,
                        duration: 14000,
                    });
                } else {
                    toast.success(`Ingested ${datasets.length} table${datasets.length !== 1 ? "s" : ""}`, {
                        description: parts.join(" · "),
                        duration: 12000,
                    });
                }
            }
            // iter-119x (#4): broadcast so every open Dashboard / Gallery
            // pulls fresh data without a hard reload. DashboardDetailView
            // (and gallery prebuilt-template tiles) listen for this event.
            window.dispatchEvent(new CustomEvent("data:ingested", {
                detail: { connectorId, datasets: datasets.length, rows: datasets.reduce((a, d) => a + (d.rows || 0), 0) },
            }));
            window.dispatchEvent(new CustomEvent("dashboard:refresh"));
            // iter-119bd Phase 4 — when the wizard has a mapping step
            // queued after this one, DON'T close — advance instead so
            // the user reviews + confirms the column mapping per dataset.
            const nextStep = STEPS[stepIdx + 1];
            if (nextStep?.key === "mapping") {
                setStepIdx(stepIdx + 1);
                return;
            }
            onDone?.({ connectorId, datasets });
        } catch (e) {
            // iter-121.3 — surface the REAL message. Previous code only
            // checked `e.response?.data?.detail`, which is undefined for
            // a plain JS Error thrown by `pollIngestJob` — so the user
            // saw a generic "Ingest failed" even when the backend had a
            // detailed reason (token expired, upstream 4xx, etc.).
            const detail = e?.response?.data?.detail
                       || (typeof e?.message === "string" && e.message)
                       || "Ingest failed";
            if (e?.isPollTimeout) {
                // Not actually a failure — the BG task may still finish.
                toast.info("Ingest still running", {
                    description: detail,
                    duration: 10000,
                });
            } else {
                toast.error(detail);
            }
        } finally { setBusy(false); }
    };

    // Configure-step check (universal, driver-agnostic): in LIVE mode every
    // visible `required` field must have a value before we save + advance.
    // Values sourced from the selected OAuth credential set count as filled;
    // OAuth tokens are skipped because they're obtained by signing in on the
    // Test step. Sample mode only needs a connection name.
    const findMissingConfigure = () => {
        const out = [];
        if (!String(name || "").trim()) out.push({ key: "__name", label: "Connection name" });
        if (isSample) return out;
        const mergeFields = oauthMerge?.fields || [];
        // No saved credential set → the OAuth client fields shown in the
        // Credentials card are needed to create one.
        const needsInlineOauth = !!oauthProvider && !oauthMerge?.has_credentials
            && !(credentialSets || []).some((cs) => !cs.deactivated);
        Object.values(fieldGroups).flat().forEach((f) => {
            const required = f.required || (needsInlineOauth && mergeFields.includes(f.key));
            if (!required || f.type === "boolean" || f.key === "use_sample_data") return;
            if (f.key === tokenField || f.key === "oauth_refresh_token") return;
            const sourced = !!oauthMerge?.has_credentials && mergeFields.includes(f.key) && !oauthOverride?.[f.key];
            const v = sourced ? oauthMerge?.values?.[f.key] : config[f.key];
            if (typeof v === "number" || typeof v === "boolean") return;
            if (v === null || v === undefined || String(v).trim() === "") {
                out.push({ key: f.key, label: toTitleLabel(f.label || f.key) });
            }
        });
        return out;
    };

    const onNext = async () => {
        const cur = STEPS[stepIdx];
        const isLast = stepIdx === STEPS.length - 1;
        if (cur.key === "configure") {
            const missingFields = findMissingConfigure();
            if (missingFields.length) {
                setMissingKeys(new Set(missingFields.map((m) => m.key)));
                const labels = missingFields.map((m) => m.label);
                toast.error(`Please enter ${labels.length > 1 ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}` : labels[0]}.`);
                return;
            }
            setMissingKeys(new Set());
            // save/update, then advance
            try {
                const savedId = await saveOrUpdateConnector();
                // iter-2026 — two-way OAuth sync. When the user fills OAuth
                // client creds directly into the wizard, the BACKEND persists
                // them as a reusable credential set under Settings → OAuth
                // Credentials (shared framework, dedup by client_id) and pins
                // this connector to it. Here we simply re-read the connector
                // to learn which set the server assigned, then refresh the
                // dropdown so the new set is listed AND selected.
                if (oauthProvider && savedId) {
                    try {
                        const cr = await api.get(`/data/connectors/${savedId}`);
                        const newSetId = cr?.data?.config?.oauth_credential_set_id || null;
                        await loadCredentialSets(newSetId);
                        if (newSetId) setConfig((c) => ({ ...c, oauth_credential_set_id: newSetId }));
                    } catch {
                        // Non-fatal — the dropdown will refresh on next open.
                    }
                }
                setStepIdx(stepIdx + 1);
            } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
            return;
        }
        if (cur.key === "test") {
            // iter-108: auto-fire Test if not already passed (per user
            // request — option b). If the test passes, persist + advance;
            // if it fails the toast surfaces and we stay on the step.
            if (!testResult?.ok) {
                const ok = await runTest();
                if (!ok) return;
            }
            // iter-107: persist the config NOW — DatabasePicker only mutates
            // local state via updateCfg("database", name). Without this PATCH
            // the backend's /schemas + /tables routes use the stale config
            // saved at step 0 (no database) and queries land in `master`,
            // which then surfaces the system tables (spt_fallback_*,
            // MSreplication_options, spt_monitor) instead of the user's DB.
            try {
                await saveOrUpdateConnector();
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not persist database selection");
                return;
            }
            // Reset schema cache so the prefetch effect re-runs against the
            // newly-saved database (same connectorId but different cfg).
            setSchemas([]);
            setExpandedSchemas({});
            setTablesBySchema({});
            setSelected([]);
            if (isLast) { await finalizeIngest(); return; }
            setStepIdx(stepIdx + 1);
            return;
        }
        if (cur.key === "accounts") {
            // iter-118c: persist the selected account(s) before advancing so
            // /schemas + /tables + ingest scope to the chosen account.
            if (selectedAccountIds.length === 0) {
                toast.error("Pick at least one account to continue");
                return;
            }
            // iter-119ay — driver-aware primary key field. Meta Ads stores
            // the primary account on `ad_account_id`; GA4 uses `property_id`.
            // We set whichever field the driver schema declares so downstream
            // helpers (e.g. _property(cfg) / _account(cfg)) resolve correctly.
            const isGA4 = driver?.key === "ga4";
            // iter-121.177 — For YAML drivers (Toast, 7shifts, any spec
            // with `accounts.enabled + discovery_endpoint`), the
            // discovery response includes a `values` dict per account
            // carrying every field the ingest engine needs to
            // interpolate ${var}s at request time (e.g.
            // `{restaurantGuid: "abc..."}` for Toast). We reshape the
            // selection into `cfg.accounts = [{name, values}]` — the
            // exact shape `sync_table_async` fans out over — so the
            // ingest engine hits every selected location without
            // needing a second discovery call OR any Toast-specific
            // code.
            const isYamlAccountsFlow = accounts.some((a) => a && typeof a === "object" && a.values);
            const yamlAccounts = isYamlAccountsFlow
                ? accounts
                      .filter((a) => selectedAccountIds.includes(a.id || a.account_id || a.customer_id || a.property_id))
                      .map((a) => ({
                          name: a.name || String(a.id),
                          values: { ...(a.values || {}) },
                      }))
                : null;
            const nextCfg = {
                ...config,
                // Primary account = first selected; full list also stored so
                // multi-account ingest can read it later.
                ...(isGA4
                    ? { property_id: selectedAccountIds[0] }
                    : { ad_account_id: selectedAccountIds[0] }),
                selected_account_ids: selectedAccountIds,
                // Only overwrite `accounts` for YAML flows. Meta / GA4
                // don't use this shape.
                ...(yamlAccounts ? { accounts: yamlAccounts } : {}),
            };
            setConfig(nextCfg);
            try {
                await api.patch(`/data/connectors/${connectorId}`, { config: nextCfg });
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not save account selection");
                return;
            }
            // Reset cached schema since account changed
            setSchemas([]);
            setExpandedSchemas({});
            setTablesBySchema({});
            setSelected([]);
            if (isLast) { await finalizeIngest(); return; }
            setStepIdx(stepIdx + 1);
            return;
        }
        if (cur.key === "date_range") {
            // iter-119u: persist sync_settings.date_range. If this is the
            // last step (OA marketing / file flow), finalize ingest immediately.
            try {
                await api.patch(`/data/connectors/${connectorId}`, {
                    sync_settings: { ...(resume?.sync_settings || {}), date_range: dateRange },
                });
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not save date range");
                return;
            }
            if (isLast) { await finalizeIngest({ selectedTables: (hasTablesStep || hasFilesStep) && selected.length ? selected : null }); return; }
            setStepIdx(stepIdx + 1);
            return;
        }
        if (cur.key === "tables") {
            if (selected.length === 0) { toast.error("Pick at least one table"); return; }
            // iter-121.10 — When the relationships step is absent (non-SA flow:
            // Configure → Test → Tables → Mapping), ingest fires HERE instead
            // of on relationships. Without this branch, advancing the tables
            // step would land the user on Mapping with no datasets, where the
            // step waits forever on `ingestedDatasets` ("Running ingest…"
            // spinner that never resolves). isLast also still triggers ingest
            // for any future flow where Tables is genuinely the last step.
            const nextStep = STEPS[stepIdx + 1];
            if (isLast || nextStep?.key === "mapping") {
                await finalizeIngest({ selectedTables: selected });
                return;
            }
            // infer on advance (next step is relationships — SA flow)
            setBusy(true);
            try {
                const r = await api.post(`/data/connectors/${connectorId}/relationships/infer`,
                    { tables: selected.map((s) => ({ schema: s.schema, name: s.name })) });
                setRelationships(r.data.relationships || []);
            } finally { setBusy(false); }
            setStepIdx(stepIdx + 1);
            return;
        }
        // iter-121.170 — Pick Files step for cloud-storage drivers
        // (S3, SFTP, OneDrive, Google Drive, Azure Blob). Uses the same `selected`
        // array as `tables`, so downstream relationships / mapping / ingest work
        // consistently according to the driver's configured wizard_steps.
        if (cur.key === "files") {
            if (selected.length === 0) { toast.error("Pick at least one file"); return; }
            const nextStep = STEPS[stepIdx + 1];
            if (isLast || nextStep?.key === "mapping") {
                await finalizeIngest({ selectedTables: selected });
                return;
            }
            if (nextStep?.key === "relationships") {
                setBusy(true);
                try {
                    const r = await api.post(`/data/connectors/${connectorId}/relationships/infer`,
                        { tables: selected.map((s) => ({ schema: s.schema || "", name: s.name })) });
                    setRelationships(r.data.relationships || []);
                } catch {
                    // Relationship inference is best-effort for files —
                    // an empty result is fine, the SA can add joins manually.
                    setRelationships([]);
                } finally { setBusy(false); }
            }
            setStepIdx(stepIdx + 1);
            return;
        }
        if (cur.key === "relationships") {
            // iter-121.165 — SQL drivers include a `mapping` step AFTER
            // relationships. In that case, relationships is a pure
            // review step and ingest is deferred to the mapping step
            // (so the Data Model choice picked there is baked into
            // sync_settings BEFORE ingest fires). For drivers without
            // a mapping step (marketing OAuth flows), relationships
            // is still the last step and still triggers ingest here.
            const nextStep = STEPS[stepIdx + 1];
            if (nextStep?.key === "mapping") {
                setStepIdx(stepIdx + 1);
                return;
            }
            if (isLast) {
                if (ingestedDatasets.length > 0) {
                    onDone?.({ connectorId, datasets: ingestedDatasets });
                    return;
                }
            }
            await finalizeIngest({ selectedTables: selected });
            return;
        }
        // iter-119bd Phase 4 — mapping step behaviour:
        //   • Pre-ingest (SQL flow, no datasets yet): fires ingest with
        //     the picked data_model_slug baked into sync_settings, then
        //     the button auto-flips to "Done" (see WizardFooter label
        //     logic below) so the user can review + dismiss.
        //   • Post-ingest (button already flipped): advances to next step (Relationships) if not last, else closes wizard.
        if (cur.key === "mapping") {
            if (!ingestedDatasets.length) {
                await finalizeIngest({ selectedTables: selected });
                return;
            }
            if (!isLast) {
                const nextStep = STEPS[stepIdx + 1];
                if (nextStep?.key === "relationships") {
                    setBusy(true);
                    try {
                        const r = await api.post(`/data/connectors/${connectorId}/relationships/infer`,
                            { tables: selected.map((s) => ({ schema: s.schema || "", name: s.name })) });
                        setRelationships(r.data.relationships || []);
                    } catch {
                        // ignore or keep existing
                    } finally { setBusy(false); }
                }
                setStepIdx(stepIdx + 1);
                return;
            }
            onDone?.({ connectorId, datasets: ingestedDatasets });
            return;
        }
    };

    // ---------- Step 2 : Test ----------
    // iter-94: harden against the silent-no-op path where the user hits
    // Test before the connector was saved (e.g. they advanced steps via
    // some other path). Surface real error shape from any backend layer
    // (auth, network, driver-side exception, timeout) so they see WHY.
    const runTest = async () => {
        if (!connectorId) {
            setTestResult({
                ok: false,
                message: "Save the connector first (use 'Next' on the Configure step). The Test button needs a saved id to run against.",
            });
            return false;
        }
        setBusy(true); setTestResult(null);
        try {
            const r = await api.post(`/data/connectors/${connectorId}/test`, null, {
                timeout: 30000,
            });
            setTestResult(r.data);
            return !!r.data?.ok;
        } catch (e) {
            let msg;
            if (e?.code === "ECONNABORTED") {
                // iter-121.174 — Generic timeout message. The old text was
                // SQL-specific ("SQL host") which was wrong for REST /
                // OAuth / API-key / SFTP / S3 connectors. The framework
                // now covers many driver families, so the message must
                // be transport-agnostic.
                msg = "Test timed out after 30s. The upstream host was unreachable or a firewall silently dropped the connection.";
            } else if (e?.code === "ERR_NETWORK") {
                // iter-121.174 — Same reason: driver-agnostic wording.
                msg = "Network error — the backend could not reach the upstream host. Check that the host/URL is reachable from this server (firewall, VPN, proxy, DNS, or security group).";
            } else if (e?.response?.status === 401 || e?.response?.status === 403) {
                msg = `Auth error (${e.response.status}): ${e.response.data?.detail || "your session may have expired — please log out and back in."}`;
            } else if (e?.response?.data?.detail) {
                msg = e.response.data.detail;
            } else if (e?.response?.data?.message) {
                msg = e.response.data.message;
            } else {
                msg = e?.message || "Test failed (no error details)";
            }
            setTestResult({ ok: false, message: msg });
            return false;
        } finally { setBusy(false); }
    };

    // ---------- Step 3 : Browse tables ----------
    const toggleSchema = async (s) => {
        const isOpen = !!expandedSchemas[s];
        setExpandedSchemas((p) => ({ ...p, [s]: !isOpen }));
        if (!isOpen && !tablesBySchema[s]) {
            setBusy(true);
            try {
                const r = await api.get(`/data/connectors/${connectorId}/tables`,
                    { params: { schema: s } });
                const tables = r.data?.tables || [];
                const hiddenSaOnly = r.data?.hidden_sa_only || [];
                setTablesBySchema((p) => ({ ...p, [s]: tables }));
                // Pre-select all tables on first load so the user sees every
                // entity ticked by default. They can uncheck to skip ingest.
                // Skip if user already has a selection (resume / re-open case).
                if (selected.length === 0 && tables.length > 0) {
                    const preselect = tables.map((t) => {
                        const updatedAt = (t.columns || []).find((c) => c.name === "updated_at");
                        const pk = (t.columns || []).find((c) => c.is_pk);
                        return { schema: s, name: t.name, incremental_col: updatedAt?.name || pk?.name || "" };
                    });
                    // iter-120 round-4 follow-up #8 — silently include any
                    // hidden SA-only tables so non-SA users still ingest
                    // every breakdown variant.
                    for (const h of hiddenSaOnly) {
                        preselect.push({ schema: h.schema, name: h.name, incremental_col: "" });
                    }
                    setSelected(preselect);
                }
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not list tables");
            } finally { setBusy(false); }
        }
    };

    const isTableSelected = (schema, name) =>
        selected.some((t) => t.schema === schema && t.name === name);
    const toggleTable = (schema, name, cols, rowCountEstimate) => {
        if (isTableSelected(schema, name)) {
            setSelected(selected.filter((t) => !(t.schema === schema && t.name === name)));
        } else {
            // Default cursor column: prefer `updated_at`, else first PK
            const updatedAt = cols.find((c) => c.name === "updated_at");
            const pk = cols.find((c) => c.is_pk);
            const cursor = updatedAt?.name || pk?.name || "";
            // iter-121.4 — carry row_count_estimate through to the BE so
            // the async-vs-sync ingest decision can use it. The BE's
            // `should_run_async` reads `t.row_count_estimate` per
            // selected_table and async-fires when sum exceeds 25k rows
            // (env: CONNECTOR_INGEST_ASYNC_THRESHOLD_ROWS).
            setSelected([
                ...selected,
                {
                    schema, name, incremental_col: cursor,
                    ...(rowCountEstimate != null ? { row_count_estimate: rowCountEstimate } : {}),
                },
            ]);
        }
    };
    const setCursor = (schema, name, col) => {
        setSelected(selected.map((t) => (t.schema === schema && t.name === name ? { ...t, incremental_col: col } : t)));
    };

    // iter-121.153 — per-schema bulk select helpers. Tables are auto-
    // selected on first expand; these let the user clear an entire schema
    // in one click (and re-select all just as fast). Available to every
    // role (SA + non-SA) per user request.
    const selectAllInSchema = (schema) => {
        const tables = tablesBySchema[schema] || [];
        const additions = tables
            .filter((t) => !isTableSelected(schema, t.name))
            .map((t) => {
                const updatedAt = (t.columns || []).find((c) => c.name === "updated_at");
                const pk = (t.columns || []).find((c) => c.is_pk);
                return {
                    schema, name: t.name,
                    incremental_col: updatedAt?.name || pk?.name || "",
                    ...(t.row_count_estimate != null ? { row_count_estimate: t.row_count_estimate } : {}),
                };
            });
        if (additions.length) setSelected([...selected, ...additions]);
    };
    const deselectAllInSchema = (schema) => {
        setSelected(selected.filter((t) => t.schema !== schema));
    };

    const selectedQualifiedTables = selected.map((s) => (s.schema ? `${s.schema}.${s.name}` : s.name));
    const allColsFor = (qualified) => {
        if (!qualified) return [];
        const match = selected.find((s) => (s.schema ? `${s.schema}.${s.name}` : s.name) === qualified);
        if (match) {
            const schemaTables = tablesBySchema[match.schema || ""] || [];
            const found = schemaTables.find((t) => t.name === match.name);
            if (found?.columns) return found.columns;
        }
        if (qualified.includes(".")) {
            const idx = qualified.indexOf(".");
            const s = qualified.substring(0, idx);
            const n = qualified.substring(idx + 1);
            return (tablesBySchema[s] || []).find((t) => t.name === n)?.columns || [];
        }
        return (tablesBySchema[""] || []).find((t) => t.name === qualified)?.columns || [];
    };

    // ---------- Step 4 : Relationships ----------
    const addRel = () => setRelationships([...relationships, {
        from_table: "", from_column: "", to_table: "", to_column: "",
        type: "one_to_many", confidence: 1.0, source: "user", note: "Manually added",
    }]);
    const updateRel = (idx, patch) => setRelationships(relationships.map((r, i) => i === idx ? { ...r, ...patch } : r));
    const deleteRel = (idx) => setRelationships(relationships.filter((_, i) => i !== idx));

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className={`${STEPS.length >= 6 ? "max-w-6xl" : "max-w-5xl"} p-0 gap-0`} data-testid="connector-wizard"
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}>
                {/* Header */}
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
                    <DialogTitle className="flex items-center gap-3 text-base">
                        <div className="w-8 h-8 bg-secondary rounded-sm flex items-center justify-center overflow-hidden p-1">
                            <ConnectorIcon iconUrl={driver.icon_url} category={driver.category} name={driver.name} className="max-w-full max-h-full" />
                        </div>
                        <span>{connectorId ? `Manage ${name || driver.name}` : `Connect ${driver.name}`}</span>
                    </DialogTitle>
                    <DialogDescription className="sr-only">Configure, test, pick tables, and review relationships.</DialogDescription>
                </DialogHeader>

                {/* Step pills */}
                <div className="px-6 pt-4 pb-3 flex items-center gap-2 flex-wrap">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        const done = i < stepIdx;
                        const active = i === stepIdx;
                        return (
                            <div key={s.key} className="flex items-center gap-2">
                                <div className={`h-6 w-6 rounded-full border flex items-center justify-center text-[11px] font-mono ${
                                    done ? "bg-[hsl(var(--success))]/15 border-[hsl(var(--success))] text-[hsl(var(--success))]" :
                                    active ? "bg-primary text-primary-foreground border-primary" :
                                    "border-border text-muted-foreground"
                                }`}>
                                    {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                                </div>
                                <span className={`text-xs ${active ? "font-semibold" : "text-muted-foreground"}`}>{s.label}</span>
                                {i < STEPS.length - 1 && <div className="h-px w-6 bg-border mx-1" />}
                            </div>
                        );
                    })}
                </div>

                {/* Body */}
                <div className="px-6 py-4 max-h-[65vh] overflow-y-auto">
                    {step.key === "configure" && (
                        <ConfigureStep ctx={{
                            name, setName: setNameAndClear, driver, fieldGroups, oauthMerge, oauthOverride,
                            setOauthOverride, config, updateCfg, isSample,
                            oauthProvider, credentialSets, missingKeys,
                        }} FieldInput={FieldInput} />
                    )}

                    {step.key === "test" && (
                        <TestStep ctx={{
                            oauthProvider, isSample, hasOAuthToken, busy, oauthMerge, driver, config,
                            startOAuth, runTest, testResult, updateCfg, setOauthMerge, api,
                        }} InlineOAuthCredsCard={InlineOAuthCredsCard} DatabasePicker={DatabasePicker} />
                    )}

                    {step.key === "accounts" && (
                        driver?.key === "redshift" ? (
                            <RedshiftWorkgroupStep ctx={{
                                accountsLoading, accountsError, accounts, selectedAccountIds, setSelectedAccountIds,
                            }} />
                        ) : (
                            <AccountsStep ctx={{
                                accountsLoading, accountsError, accounts, selectedAccountIds, setSelectedAccountIds,
                            }} />
                        )
                    )}

                    {step.key === "date_range" && (
                        <DateRangeStep ctx={{
                            dateRange,
                            setDateRange,
                            previewRows: async (dr) => {
                                const r = await api.post("/data/connectors-aux/preview-rowcount", {
                                    connector_key: driver.key,
                                    connector_id: connectorId,
                                    config,
                                    date_range: dr,
                                });
                                return r.data;
                            },
                        }} />
                    )}

                    {step.key === "tables" && (
                        <TablesStep ctx={{
                            schemas, expandedSchemas, tablesBySchema, selected, busy, driver,
                            toggleSchema, toggleTable, setCursor, isTableSelected,
                            isSuperAdmin, selectAllInSchema, deselectAllInSchema,
                            dataModelSlug, modelTargets,
                        }} />
                    )}

                    {/* iter-121.170 — Pick Files step for cloud-storage drivers.
                        Same `selected` array as tables ({schema, name, ...})
                        with schema="" and name=<file path>. */}
                    {step.key === "files" && (
                        <FileBrowserStep ctx={{
                            filesByPath, expandedPaths, selected, busy, driver,
                            toggleFolder, toggleFile, toggleFolderSelection, isFileSelected,
                            isSuperAdmin,
                        }} />
                    )}


                    {step.key === "relationships" && (
                        <RelationshipsStep ctx={{
                            driver,
                            relationships, selected, selectedQualifiedTables,
                            addRel, updateRel, deleteRel, allColsFor,
                            factTable, setFactTable,
                            enableSchedule, scheduleHours, setEnableSchedule, setScheduleHours,
                            dataModels, dataModelSlug, setDataModelSlug, setDataModels, modelTargets,
                            legacyTarget: resume && !resume.model_binding_strict && !resume.model_selection_required ? (resume?.sync_settings?.data_model_slug || driver?.target_model_slug) : null,
                        }} />
                    )}

                    {step.key === "mapping" && (
                        <MappingStep ctx={{
                            ingestedDatasets,
                            // iter-121.165 — Data Model picker for SQL flows.
                            // Same state the marketing RelationshipsStep uses,
                            // rendered here PRE-ingest so the SA picks the
                            // model before rows are written.
                            driver,
                            dataModels, dataModelSlug, setDataModelSlug, setDataModels,
                            selected,
                        }} />
                    )}

                </div>

                {/* Footer */}
                <WizardFooter ctx={{
                    onClose, onBack, stepIdx, setStepIdx, onNext, busy,
                    stepsLen: STEPS.length, enableSchedule,
                    currentStepKey: step?.key,
                    nextStepKey: STEPS[stepIdx + 1]?.key,
                    // iter-121.165 — Post-ingest state so the mapping
                    // step's CTA flips from "Ingest" → "Done".
                    ingestDone: ingestedDatasets.length > 0,
                }} />
            </DialogContent>
        </Dialog>
    );
}

// ---------- Small helpers ----------
// One-line help under every field (full text on hover) — keeps the
// Configure page scannable for every driver, including future ones.
function FieldHelp({ text }) {
    if (!text) return null;
    return <div className="text-[11px] text-muted-foreground mt-1 leading-snug truncate" title={text}>{text}</div>;
}
function FieldLabel({ field, htmlFor }) {
    return (
        <label className="field-label truncate" htmlFor={htmlFor} data-testid={`wiz-label-${field.key}`}>
            {toTitleLabel(field.label || field.key)}{field.required && <span className="text-primary"> *</span>}
        </label>
    );
}

function FieldInput({ field, value, onChange, disabled, hint, onOverride, invalid }) {
    const border = invalid ? "border-destructive" : "border-border";
    const shared = `w-full h-9 px-3 bg-transparent border ${border} rounded-sm text-sm font-mono placeholder:font-sans placeholder:text-muted-foreground/60 disabled:opacity-60 disabled:cursor-not-allowed`;
    // bg-background is used specifically for <select> so the native browser
    // dropdown options list also inherits the dark theme background color.
    // bg-transparent on a <select> only affects the closed state — the options
    // popup always renders with the element's computed background, so we need
    // an explicit dark-aware colour here.
    const selectShared = `w-full h-9 px-3 bg-background border ${border} rounded-sm text-sm disabled:opacity-60 disabled:cursor-not-allowed`;
    const inputId = `wiz-input-${field.key}`;
    const placeholder = field.placeholder || (field.required ? "" : "Optional");
    if (field.type === "boolean") {
        return (
            <label className={`flex items-center gap-2 py-0.5 min-w-0 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
                <Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} disabled={disabled}
                    data-testid={`wiz-field-${field.key}`} />
                <span className="text-[13px] leading-tight shrink-0">{field.label}</span>
                {field.help && (
                    <span className="text-[11px] text-muted-foreground truncate" title={field.help}>— {field.help}</span>
                )}
            </label>
        );
    }
    // Phase-1 (Refresh-Token UX) — when the backend has told us this
    // password/secret field already has a value on file (via the SAVED
    // sentinel), don't render an empty-looking password box. Show a
    // "Saved · Replace" pill so the user knows they don't need to
    // re-enter anything. Clicking "Replace" clears the field back to a
    // normal password input for entering a new value.
    const savedOnFile = field.type === "password" && isSavedSentinel(value);
    if (savedOnFile) {
        return (
            <div className="min-w-0">
                <div className="flex items-center justify-between mb-1.5 min-w-0">
                    <FieldLabel field={field} />
                </div>
                <div
                    className="flex items-center justify-between h-9 px-3 bg-secondary/30 border border-border rounded-sm"
                    data-testid={`wiz-field-${field.key}-saved`}
                >
                    <div className="flex items-center gap-2 text-[12px] min-w-0">
                        <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))] shrink-0" />
                        <span className="text-muted-foreground truncate" title="Leave as-is to keep the saved value.">Saved</span>
                    </div>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onChange("")}
                        className="text-[10px] uppercase tracking-wider font-medium text-primary hover:underline disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                        data-testid={`wiz-field-${field.key}-replace`}
                    >
                        Replace
                    </button>
                </div>
                <FieldHelp text={field.help} />
            </div>
        );
    }
    return (
        <div className="min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1.5 min-w-0">
                <FieldLabel field={field} htmlFor={inputId} />
                {onOverride && (
                    <button
                        type="button"
                        onClick={onOverride}
                        className="text-[9px] uppercase tracking-wider text-primary hover:underline shrink-0"
                        data-testid={`wiz-field-${field.key}-override`}
                    >
                        Override
                    </button>
                )}
            </div>
            {field.type === "textarea" ? (
                <textarea id={inputId} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
                    disabled={disabled} placeholder={placeholder} aria-invalid={invalid || undefined}
                    className={shared.replace("h-9", "h-auto py-2")} data-testid={`wiz-field-${field.key}`} />
            ) : field.type === "select" ? (
                <select id={inputId} value={value || ""} onChange={(e) => onChange(e.target.value)}
                    disabled={disabled} aria-invalid={invalid || undefined}
                    className={selectShared} data-testid={`wiz-field-${field.key}`}>
                    {!field.required && <option value="">—</option>}
                    {(field.options || []).map((o) => {
                        const val = typeof o === "object" ? o.value : o;
                        const lbl = typeof o === "object" ? (o.label ?? o.value) : o;
                        return <option key={val} value={val}>{lbl}</option>;
                    })}
                </select>
            ) : (
                <input id={inputId} type={field.type === "password" ? "password" : field.type === "number" ? "number" : "text"}
                    value={value ?? ""} onChange={(e) => onChange(field.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                    disabled={disabled} placeholder={placeholder} aria-invalid={invalid || undefined}
                    className={shared} data-testid={`wiz-field-${field.key}`} />
            )}
            {hint ? (
                <div className="text-[11px] text-primary/80 mt-1 flex items-center gap-1 min-w-0" data-testid={`wiz-field-${field.key}-hint`}>
                    <Link2 className="h-2.5 w-2.5 shrink-0" /> <span className="truncate">{hint}</span>
                </div>
            ) : (
                <FieldHelp text={field.help} />
            )}
        </div>
    );
}


// iter-93 — Discover databases on the connected server (SSMS-style picker).
// Only renders for drivers whose category is "Database" — skipped for SaaS
// connectors (Google Ads, GA4, Stripe, …) where there's no notion of a
// pickable database. Drivers that don't support list_databases() return [],
// in which case we render nothing.
function DatabasePicker({ driver, cfg, onPick }) {
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    if (driver.category !== "Database") return null;
    const discover = async () => {
        setLoading(true); setErr(null);
        try {
            const r = await api.post("/data/connectors/preview/databases", {
                connector_key: driver.key,
                config: cfg || {},
            });
            setList(r.data.databases || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to list databases");
        } finally { setLoading(false); }
    };
    return (
        <div className="mt-5 max-w-md mx-auto border border-border rounded-sm p-4 text-left bg-secondary/20" data-testid="wiz-db-picker">
            <div className="flex items-center justify-between mb-2">
                <span className="eyebrow">Databases on this server</span>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={discover} disabled={loading} data-testid="wiz-db-discover">
                    {loading ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Listing…</> : "Discover"}
                </Button>
            </div>
            {err && <div className="text-xs text-destructive font-mono">{err}</div>}
            {list === null && !err && (
                <div className="text-[11px] text-muted-foreground">
                    Click <b>Discover</b> to list databases the configured login can see.
                    Pick one to scope schemas/tables in the next step.
                </div>
            )}
            {list && list.length === 0 && (
                <div className="text-[11px] text-muted-foreground">
                    No databases visible to this login (system DBs are hidden).
                    Confirm the user has <code className="font-mono">db_datareader</code> on at least one user database.
                </div>
            )}
            {list && list.length > 0 && (
                <div className="grid grid-cols-2 gap-1.5 mt-1">
                    {list.map((name) => {
                        const active = (cfg.database || "") === name;
                        return (
                            <button
                                key={name}
                                onClick={() => onPick(name)}
                                className={`text-left px-2.5 py-2 rounded-sm border text-[12px] font-mono transition-colors ${
                                    active
                                        ? "border-primary bg-primary/10 text-primary"
                                        : "border-border hover:border-primary/50 hover:bg-secondary/40"
                                }`}
                                data-testid={`wiz-db-pick-${name}`}
                            >
                                {active && "✓ "}{name}
                            </button>
                        );
                    })}
                </div>
            )}
            {cfg.database && (
                <div className="mt-2 text-[10px] font-mono text-muted-foreground">
                    Active database: <span className="text-foreground">{cfg.database}</span>
                </div>
            )}
        </div>
    );
}


/* ─────────────────────────────── iter-119 — Gap 2b ─────────────────────
 * Inline OAuth-credentials editor used inside the Test step when a connector
 * requires OAuth but the tenant hasn't saved Client ID / Secret yet. Lets SA
 * paste credentials without closing the wizard. Saves via the same endpoint
 * used by Settings → OAuth Credentials (`PUT /oauth/credentials/{provider}`).
 */
function InlineOAuthCredsCard({ provider, hasCredentials, onSaved }) {
    const [open, setOpen] = React.useState(!hasCredentials);
    const [clientId, setClientId] = React.useState("");
    const [clientSecret, setClientSecret] = React.useState("");
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        // Expand by default whenever credentials are missing.
        if (!hasCredentials) setOpen(true);
    }, [hasCredentials]);

    const save = async () => {
        if (!clientId.trim() || !clientSecret.trim()) {
            toast.error("Both Client ID and Client Secret are required");
            return;
        }
        setSaving(true);
        try {
            await api.put(`/oauth/credentials/${provider}`, {
                client_id: clientId.trim(),
                client_secret: clientSecret.trim(),
            });
            toast.success("OAuth credentials saved");
            setClientId("");
            setClientSecret("");
            await onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mb-3 border border-border bg-card/50 rounded-sm" data-testid="wiz-inline-oauth-card">
            <button
                type="button"
                onClick={() => setOpen((p) => !p)}
                className="w-full px-3 py-2 flex items-center justify-between text-[11px]"
                data-testid="wiz-inline-oauth-toggle"
            >
                <span className="flex items-center gap-1.5">
                    <KeyRound className="h-3 w-3 text-primary" />
                    <span className="font-medium">{hasCredentials ? "OAuth credentials on file" : "Set up OAuth credentials"}</span>
                    {hasCredentials && (
                        <Badge variant="outline" className="font-mono text-[9px] border-[hsl(var(--success))] text-[hsl(var(--success))]">SAVED</Badge>
                    )}
                </span>
                <span className="text-muted-foreground">{open ? "Hide" : (hasCredentials ? "Update" : "Show")}</span>
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-2 border-t border-border" data-testid="wiz-inline-oauth-body">
                    <p className="text-[10px] text-muted-foreground pt-2">
                        Paste your <code className="font-mono">Client ID</code> and <code className="font-mono">Client Secret</code> from the provider's app dashboard. These are stored tenant-wide and reused across every {provider} connector + the OAuth flow.
                    </p>
                    <div>
                        <label className="eyebrow mb-1 block text-[10px]">Client ID</label>
                        <input
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            placeholder={hasCredentials ? "(saved — leave blank to keep)" : "e.g. 1317652793577523"}
                            className="w-full h-8 px-2 bg-transparent border border-border rounded-sm text-[11px] font-mono"
                            data-testid="wiz-inline-oauth-client-id"
                        />
                    </div>
                    <div>
                        <label className="eyebrow mb-1 block text-[10px]">Client Secret</label>
                        <input
                            type="password"
                            value={clientSecret}
                            onChange={(e) => setClientSecret(e.target.value)}
                            placeholder={hasCredentials ? "(saved — leave blank to keep)" : "••••••••••••••••"}
                            className="w-full h-8 px-2 bg-transparent border border-border rounded-sm text-[11px] font-mono"
                            data-testid="wiz-inline-oauth-client-secret"
                        />
                    </div>
                    <div className="flex items-center justify-between gap-2 pt-1">
                        <a
                            href="/settings?tab=oauth"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] text-primary hover:underline"
                            data-testid="wiz-inline-oauth-settings-link"
                        >
                            Open full Settings → OAuth tab ↗
                        </a>
                        <Button
                            size="sm"
                            className="rounded-sm h-7 text-[11px]"
                            onClick={save}
                            disabled={saving}
                            data-testid="wiz-inline-oauth-save"
                        >
                            {saving ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Saving…</> : "Save credentials"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
